import Device from "../utils/Device";
import onResize from "../utils/onResize";
import { getHardwareInfo, getMediaPreferences, getNetworkInfo, onCapabilitiesChange, } from "../utils/Capabilities";
import { shouldPlayBackgroundVideo, } from "../utils/backgroundVideoPolicy";
const selector = ".js-bg-video";
const stateAttribute = "data-bg-video-state";
/** How long a video may take to produce its first frame before we give up on it. */
const ATTACH_WATCHDOG_MS = 8000;
/** How long a download may sit stalled before we give up on it. */
const STALL_MS = 4000;
function readEnvironment() {
    const { prefersReducedMotion, prefersReducedData } = getMediaPreferences();
    const { saveData, effectiveType } = getNetworkInfo();
    const { cores, memoryGB } = getHardwareInfo();
    return {
        prefersReducedMotion,
        prefersReducedData,
        saveData,
        effectiveType,
        deviceMemoryGB: memoryGB,
        cores,
        isSmallScreen: Device.screen.isMobileSize(),
    };
}
function noop() {
    // Deliberately empty - see `resume`.
}
/**
 * Owns one background video element.
 *
 * The element is served without `src` and without `autoplay`, so nothing downloads
 * until we say so and we keep control of the ordering. Whenever we back out we clear
 * `src` and call `load()`, which is what actually aborts an in-flight media fetch and
 * releases the decoder buffer - the poster stays, so the hero still looks intact.
 */
function createController(video) {
    let attached = false;
    let playing = false;
    /** Set once the video proved it cannot play here; we then never retry. */
    let abandoned = false;
    let watchdog;
    let stallTimer;
    function setState(state) {
        video.setAttribute(stateAttribute, state);
    }
    function clearTimers() {
        window.clearTimeout(watchdog);
        window.clearTimeout(stallTimer);
        watchdog = undefined;
        stallTimer = undefined;
    }
    function detach(state) {
        clearTimers();
        attached = false;
        playing = false;
        video.pause();
        video.removeAttribute("src");
        // Aborts the pending download and frees the decoder; `poster` is untouched.
        video.load();
        setState(state);
    }
    /** Policy said no. Reversible: if conditions improve we may attach after all. */
    function block(reason) {
        if (!attached) {
            setState(`blocked:${reason}`);
            return;
        }
        detach(`blocked:${reason}`);
    }
    /** The video itself failed. Never retried - a flaky link must not cause a storm. */
    function abort(reason) {
        abandoned = true;
        detach(`aborted:${reason}`);
    }
    function markPlaying() {
        clearTimers();
        playing = true;
        setState("playing");
    }
    function onStalled() {
        if (!attached || playing)
            return;
        window.clearTimeout(stallTimer);
        stallTimer = window.setTimeout(() => abort("stalled"), STALL_MS);
    }
    function onProgress() {
        window.clearTimeout(stallTimer);
        stallTimer = undefined;
    }
    function attach() {
        const src = video.dataset.src;
        if (!src)
            return;
        attached = true;
        video.setAttribute("src", src);
        video.load();
        watchdog = window.setTimeout(() => {
            if (video.readyState >= video.HAVE_FUTURE_DATA) {
                markPlaying();
                return;
            }
            abort("timeout");
        }, ATTACH_WATCHDOG_MS);
        const started = video.play();
        // Safari resolves this only once playback really begins, and rejects it outright
        // under Low Power Mode - the one signal iOS does give us about a video it will
        // never play.
        if (started && typeof started.then === "function") {
            started.then(markPlaying, () => abort("play-rejected"));
        }
    }
    function evaluate() {
        if (abandoned)
            return;
        const decision = shouldPlayBackgroundVideo(readEnvironment());
        if (!decision.play) {
            block(decision.reason);
            return;
        }
        if (!attached)
            attach();
    }
    /** Once the bytes are downloaded there is nothing left to save by tearing down. */
    function pause() {
        if (!playing)
            return;
        video.pause();
    }
    function resume() {
        if (!playing)
            return;
        const started = video.play();
        if (started && typeof started.then === "function")
            started.catch(noop);
    }
    video.addEventListener("playing", markPlaying);
    video.addEventListener("stalled", onStalled);
    video.addEventListener("progress", onProgress);
    video.addEventListener("timeupdate", onProgress);
    video.addEventListener("error", () => {
        if (attached)
            abort("error");
    });
    // `suspend` is deliberately not handled: it fires normally once the buffer is full.
    return { evaluate, pause, resume, element: video };
}
function watchVisibility(controllers) {
    document.addEventListener("visibilitychange", () => {
        controllers.forEach((controller) => document.hidden ? controller.pause() : controller.resume());
    });
}
/** A hero that has scrolled away keeps decoding frames for nothing. */
function watchIntersection(controllers) {
    if (typeof IntersectionObserver === "undefined")
        return;
    const byElement = new Map();
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const controller = byElement.get(entry.target);
            if (!controller)
                return;
            entry.isIntersecting ? controller.resume() : controller.pause();
        });
    }, { threshold: 0 });
    controllers.forEach((controller) => {
        byElement.set(controller.element, controller);
        observer.observe(controller.element);
    });
}
export default {
    /**
     * Init background videos.
     *
     * [data-src]              Video URL, attached only once playback is allowed.
     * [data-bg-video-state]   Written back as `playing`, `blocked:<reason>`
     *                         or `aborted:<reason>`.
     */
    init: function (videoSelector = selector) {
        const videos = document.querySelectorAll(videoSelector);
        if (!videos.length)
            return;
        const controllers = Array.from(videos).map(createController);
        const evaluateAll = () => controllers.forEach((controller) => controller.evaluate());
        evaluateAll();
        // The old gate ran once, so rotating a phone never changed anything.
        onCapabilitiesChange(evaluateAll);
        onResize(evaluateAll, 200);
        watchVisibility(controllers);
        watchIntersection(controllers);
    },
};
